import React from "react";
import PropTypes from "prop-types";
import { styled, useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import Drawer from "@mui/material/Drawer";
import {
  HEADER_HEIGHT,
  DATA_BAR_HEIGHT,
  FILTERS_WIDTH,
  BODY_WIDTH,
  DETAIL_MIN_WIDTH,
  DETAIL_INLINE_BREAKPOINT,
} from "../styles/layoutSizes";

const STICKY_TOP_LEFT = HEADER_HEIGHT + DATA_BAR_HEIGHT;
const STICKY_TOP_DETAIL = HEADER_HEIGHT;

const Shell = styled("div")({
  display: "flex",
  alignItems: "flex-start",
  width: "100%",
});

const LeftStack = styled("div")({
  display: "flex",
  flexDirection: "column",
  flex: 1,
  minWidth: 0,
  [`@media (min-width: ${DETAIL_INLINE_BREAKPOINT}px)`]: {
    flex: "none",
    width: FILTERS_WIDTH + BODY_WIDTH,
  },
});

const FiltersAndBodyRow = styled("div")({
  display: "flex",
  alignItems: "flex-start",
  width: "100%",
});

const stickyPane = ({ theme, top }) => ({
  position: "sticky",
  top,
  alignSelf: "flex-start",
  height: `calc(100vh - ${top}px)`,
  overflowY: "auto",
  WebkitOverflowScrolling: "touch",
  backgroundColor: theme.palette.background.paper,
  flexShrink: 0,
});

const FiltersPane = styled("aside")(({ theme }) => ({
  position: "sticky",
  top: STICKY_TOP_LEFT,
  alignSelf: "flex-start",
  height: `calc(100vh - ${STICKY_TOP_LEFT}px)`,
  display: "flex",
  flexDirection: "column",
  backgroundColor: theme.palette.background.paper,
  flexShrink: 0,
  width: FILTERS_WIDTH,
  borderRight: `1px solid ${theme.palette.divider}`,
}));

const BodyPane = styled("main")(({ theme }) => ({
  position: "sticky",
  top: STICKY_TOP_LEFT,
  alignSelf: "flex-start",
  height: `calc(100vh - ${STICKY_TOP_LEFT}px)`,
  display: "flex",
  flexDirection: "column",
  backgroundColor: theme.palette.background.default,
  flex: 1,
  minWidth: 0,
  flexShrink: 0,
  [`@media (min-width: ${DETAIL_INLINE_BREAKPOINT}px)`]: {
    flex: "none",
    width: BODY_WIDTH,
  },
}));

const BodyInner = styled("div")(({ theme }) => ({
  flex: 1,
  minHeight: 0,
  width: "100%",
  overflowY: "auto",
  WebkitOverflowScrolling: "touch",
  padding: theme.spacing(1.5, 2, 3, 2),
  display: "flex",
  flexDirection: "column",
  minWidth: 0,
}));

const DetailPane = styled("aside")(({ theme }) => ({
  ...stickyPane({ theme, top: STICKY_TOP_DETAIL }),
  flex: 1,
  minWidth: DETAIL_MIN_WIDTH,
  borderLeft: `1px solid ${theme.palette.divider}`,
}));

function AppShell({
  dataBar,
  filters,
  bodyHeader,
  body,
  bodyFooter,
  detail,
  filtersDrawerOpen,
  onFiltersClose,
  detailOpen,
  onDetailClose,
}) {
  const theme = useTheme();
  const isWide = useMediaQuery(`(min-width: ${DETAIL_INLINE_BREAKPOINT}px)`);
  const isMedium = useMediaQuery(theme.breakpoints.up("md"));

  return (
    <>
      <Shell>
        <LeftStack>
          {dataBar}
          <FiltersAndBodyRow>
            {isMedium && <FiltersPane>{filters}</FiltersPane>}
            <BodyPane>
              {bodyHeader}
              <BodyInner id="body-pane">
                {body}
              </BodyInner>
              {bodyFooter}
            </BodyPane>
          </FiltersAndBodyRow>
        </LeftStack>
        {isWide && <DetailPane>{detail}</DetailPane>}
      </Shell>

      {!isMedium && (
        <Drawer
          anchor="left"
          open={!!filtersDrawerOpen}
          onClose={onFiltersClose}
          PaperProps={{
            sx: {
              width: 320,
              top: `${HEADER_HEIGHT}px`,
              height: `calc(100% - ${HEADER_HEIGHT}px)`,
            },
          }}
        >
          {filters}
        </Drawer>
      )}

      {!isWide && (
        <Drawer
          anchor="right"
          open={!!detailOpen}
          onClose={onDetailClose}
          PaperProps={{
            sx: {
              width: "100%",
              [`@media (min-width: ${FILTERS_WIDTH / 2 + DETAIL_MIN_WIDTH}px)`]: {
                width: DETAIL_MIN_WIDTH,
              },
              top: `${HEADER_HEIGHT}px`,
              height: `calc(100% - ${HEADER_HEIGHT}px)`,
            },
          }}
        >
          {detail}
        </Drawer>
      )}
    </>
  );
}

AppShell.propTypes = {
  dataBar: PropTypes.node,
  filters: PropTypes.node,
  bodyHeader: PropTypes.node,
  body: PropTypes.node,
  bodyFooter: PropTypes.node,
  detail: PropTypes.node,
  filtersDrawerOpen: PropTypes.bool,
  onFiltersClose: PropTypes.func,
  detailOpen: PropTypes.bool,
  onDetailClose: PropTypes.func,
};

export default AppShell;
